// eslint-disable-next-line import/no-cycle
import { UnknownError } from '@cian/peperrors';

// eslint-disable-next-line import/no-cycle
import { listenEventChannel } from './listenEventChannel';
import { openEventChannelFailed } from './openEventChannelFailed';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { currentOpenChatIDSelector } from '../../selectors';
import { setSocketStatusFailed, setSocketStatusPending, setSocketStatusSucceed } from '../../slices/socketStatusSlice';
import { EExceptionDomain } from '../../types/exceptionDomains';
import { TThunkAction } from '../../types/redux/thunk';
import { ETelemetryEvents } from '../../types/telemetry';
import { getGenericLikeError } from '../../utils/logging/getGenericLikeError';
import { retryWithDelay } from '../../utils/retryWithDelay';
import { synchronizeChatsV2 } from '../chatList/synchronizeChatsV2';
import { updateMessages } from '../messages/updateMessages';

const RETRIES_COUNT = 4;

/**
 * Открытие канала WS
 */
export const openEventChannel =
  (reconnect: boolean = false): TThunkAction<Promise<void>> =>
  async (dispatch, getState, { logger, telemetry, custom: { socket } }) => {
    if (!socket) {
      return;
    }

    const { eventChannel } = socket;

    const state = getState();

    dispatch(setSocketStatusPending());

    // Если соединение не закрыто
    if (eventChannel.hasInstance() && !eventChannel.isChannelClosed()) {
      dispatch(setSocketStatusSucceed());
      dispatch(listenEventChannel());

      return;
    }

    const startTime = new Date().getTime();

    try {
      // Делаем 4 попытки открыть соединение с задержкой в 1, 2, 3, 4 секунды
      await retryWithDelay({
        asyncFn: () => {
          if (eventChannel.isChannelClosed()) {
            return eventChannel.connectToEventChannel() as Promise<void>;
          }

          return Promise.resolve();
        },
        onError: error => {
          telemetry.increment(ETelemetryEvents.ChatWsSocketError);

          const connectError = new UnknownError({
            domain: EExceptionDomain.Ws,
            message: 'Ошибка при попытке подключения к вебсокету',
            details: error,
          });

          logger.error(connectError);
        },
        retryCount: RETRIES_COUNT,
      });

      telemetry.timing(ETelemetryEvents.ChatWsConnectionTime, Date.now() - startTime);

      dispatch(setSocketStatusSucceed());
      dispatch(listenEventChannel());

      if (reconnect) {
        const currentOpenChatID = currentOpenChatIDSelector(state);

        if (currentOpenChatID !== CHATBOT_ID && currentOpenChatID) {
          dispatch(updateMessages(currentOpenChatID, true));
        }

        dispatch(synchronizeChatsV2());
      }
    } catch (e) {
      dispatch(setSocketStatusFailed());
      dispatch(openEventChannelFailed());

      const errorToLog = getGenericLikeError({
        error: e,
        domain: EExceptionDomain.Ws,
        message: 'Ошибка при открытии соединения по вебсокету',
      });

      logger.error(errorToLog);

      return;
    }
  };
